import React from 'react';
import { IFaq } from 'ui/components/molecules/FaqAccordion';
import ServiceDetailSection from 'ui/components/organism/ServiceDetailSection';
import SocialMediaLinksSection from 'ui/components/organism/SocialMediaLinksSection';
import BreadCrumbs, { IBreadcrumbs } from 'ui/components/molecules/Breadcrumbs';
import InfoSection from '../../organism/InfoSection';
import ErrorScreen from 'ui/components/molecules/ErrorScreen';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import { IServiceContent, IServiceProduct } from '../../atoms/TypeCarModal';
import { ITermAndConditions } from '../../molecules/TermAndConditions';
import dynamic from 'next/dynamic';
import FaqAccordionNoscript from '../../molecules/FaqAccordionNoScript';

const ServiceFloatingAction = dynamic(
  () => import('ui/components/molecules/ServiceFloatingAction'),
  {
    ssr: false,
  }
);

const FaqAccordion = dynamic(() => import('ui/components/molecules/FaqAccordion'), {
  ssr: false,
});

interface ServiceDetailPageTemplateProps {
  errorCode?: number;
  serviceContent?: IServiceContent;
  serviceProductList?: IServiceProduct[];
  serviceModelTag?: Array<{ text: string; iconUrl: string; hide?: boolean }>;
  breadcrumbItems?: IBreadcrumbs[];
  faqs?: {
    faqList?: IFaq[];
  };
  titleChoosePackage?: string;
  titlePrice?: string;
  serviceType?: string;
  servicePlan?: {
    firstPeriodicServiceTitle: {
      name: string;
      value: string[];
    }[];
    firstPeriodicServiceDetail: {
      name: string;
      value: string[];
    }[];
  };
  tnc?: ITermAndConditions;
  isCMS?: boolean;
  triggerGTM?: { status: boolean };
  serviceDiscounts?: string;
  serviceProductTitle?: string;
  bodyTypeName?: string;
  feature?: string;
}

const ServiceDetailPageTemplate = ({
  errorCode,
  serviceContent,
  serviceProductList,
  serviceModelTag,
  breadcrumbItems,
  faqs,
  titleChoosePackage,
  titlePrice,
  serviceType,
  servicePlan,
  tnc,
  isCMS = false,
  triggerGTM = { status: false },
  serviceDiscounts,
  serviceProductTitle,
  bodyTypeName,
  feature,
}: ServiceDetailPageTemplateProps) => {
  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;
  const info = {
    title: serviceContent?.couponPacketServiceContent?.titleInformationBelow,
    desc: serviceContent?.couponPacketServiceContent?.descriptionInformationBelow,
  };
  const { selectedCar, selectedPackage } =
    useSelector((state: IRootState) => state.servicePackage) || {};

  // Error page
  if (errorCode) {
    return <ErrorScreen errorCode={errorCode} />;
  }

  return (
    <React.Fragment>
      <BreadCrumbs
        isCMS={isCMS}
        isMobileScreen={false}
        items={breadcrumbItems}
        position="md:top-[105px] lg:top-[105px] xl:top-[125px]"
      />

      <ServiceDetailSection
        breadCrumbTitle={breadcrumbItems?.[breadcrumbItems?.length - 1].name}
        serviceContent={serviceContent}
        serviceProductList={serviceProductList}
        serviceModelTag={serviceModelTag}
        titleChoosePackage={titleChoosePackage}
        titlePrice={titlePrice}
        serviceType={serviceType}
        servicePlan={servicePlan}
        isCMS={isCMS}
        termCondition={tnc}
        triggerGTM={triggerGTM}
        serviceProductTitle={serviceProductTitle || ''}
        serviceDiscounts={serviceDiscounts}
        bodyTypeName={bodyTypeName}
        breadcrumbItems={breadcrumbItems}
        feature={feature}
      />

      <InfoSection
        info={info}
        containerClassName={'w-full p-4'}
        headingClassName="mb-1 lg:mb-2"
        contentClassName="!text-sm lg:!text-base"
        sectionClassName="!my-3 lg:!mt-10 lg:!mb-0 !px-0"
        feature={feature + '_INFO'}
        impressionData={{
          breadcrumbItems,
          pageTitle: isCMS
            ? serviceContent?.couponPacketServiceContent?.titlePage
            : serviceContent?.couponPacketServiceContent?.titlePage?.toLowerCase() ===
                breadcrumbItems?.[breadcrumbItems?.length - 1].name?.toLowerCase()
              ? serviceContent?.couponPacketServiceContent?.titlePage
              : breadcrumbItems?.[breadcrumbItems?.length - 1].name,
        }}
      />
      <FaqAccordionNoscript
        title={serviceContent?.couponPacketServiceContent?.titleFaq}
        faqs={faqs?.faqList}
      />
      <FaqAccordion
        title={serviceContent?.couponPacketServiceContent?.titleFaq}
        faqs={faqs.faqList}
        isCMS={isCMS}
        containerClassname="my-6 px-0 lg:my-5"
        divClassName="w-full p-4"
        impressionData={{
          breadcrumbItems,
          pageTitle: isCMS
            ? serviceContent?.couponPacketServiceContent?.titlePage
            : serviceContent?.couponPacketServiceContent?.titlePage?.toLowerCase() ===
                breadcrumbItems?.[breadcrumbItems?.length - 1].name?.toLowerCase()
              ? serviceContent?.couponPacketServiceContent?.titlePage
              : breadcrumbItems?.[breadcrumbItems?.length - 1].name,
        }}
        feature={feature}
      />

      <div className="mt-4 mb-5 lg:mt-10 lg:mb-0">
        <SocialMediaLinksSection
          enableSharing={isCMS}
          shareUrl={`${PUBLIC_URL}`}
          copyLink={{
            category: {
              type: serviceType,
            },
            contentCategory: {
              name: serviceContent?.couponPacketServiceContent?.carCategory,
            },
            titlePage: serviceProductTitle || '',
            carName: selectedCar?.vehicleName,
            serviceProductTitle: serviceProductTitle || '',
          }}
          isServiceDetail={true}
          feature={feature}
          impressionData={{
            pageTitle: isCMS
              ? serviceContent?.couponPacketServiceContent?.titlePage
              : serviceContent?.couponPacketServiceContent?.titlePage?.toLowerCase() ===
                  breadcrumbItems?.[breadcrumbItems?.length - 1].name?.toLowerCase()
                ? serviceContent?.couponPacketServiceContent?.titlePage
                : breadcrumbItems?.[breadcrumbItems?.length - 1].name,
            breadCrumb: breadcrumbItems,
          }}
        />
      </div>

      <BreadCrumbs isMobileScreen={true} items={breadcrumbItems} />

      <ServiceFloatingAction
        serviceContent={serviceContent}
        serviceProductTitle={serviceProductTitle || ''}
        serviceType={serviceType}
        triggerGTM={triggerGTM}
        bodyTypeName={bodyTypeName}
        feature={feature}
        impressionData={{
          pageTitle: isCMS
            ? serviceContent?.couponPacketServiceContent?.titlePage
            : serviceContent?.couponPacketServiceContent?.titlePage?.toLowerCase() ===
                breadcrumbItems?.[breadcrumbItems?.length - 1].name?.toLowerCase()
              ? serviceContent?.couponPacketServiceContent?.titlePage
              : breadcrumbItems?.[breadcrumbItems?.length - 1].name,
          breadCrumb: breadcrumbItems,
        }}
      />
    </React.Fragment>
  );
};

export default ServiceDetailPageTemplate;
